import { WebAuth } from 'auth0-js'
import { getApiEnvironment, getLoginURLWithRedirectString, getSSOConfig } from './environment'
import { sendMFACode } from '../service/authService'
import { getCSRFToken, setCSRFToken, removeCSRFToken } from './cookieUtils'

export interface LoginResponse {
  success: boolean
  text: string
  csrftoken?: string
  errorCode?: string
}

export interface ErrorResponse {
  timestamp: number
  status: number
  error: string
  message: string
  errorCode: string
}

export interface SSOConnectionNameResponse {
  oidcConnectionName?: string
  samlConnectionName?: string
}

export interface LoginSettingsResponse {
  oidcConnectionName?: string
  samlConnectionName?: string
  mfatype?: string
  connection?: string
  tosSigned?: boolean
}

export interface OIDCAccessTokenResponse {
  status: number
  accessToken: string
  idToken: string
  refreshToken: string
  token: string
  user: string
}

export interface SAMLLoginResponse {
  user: string
  token: string
}

export interface UserInfoResponse {
  userName: string
  passwordless: boolean
  csrfToken: string
  products: string[]
  userPermissions: string[]
}

export async function login(username: string, password: string, otp?: string): Promise<LoginResponse> {
  const apiResponse = await fetch(`${getApiEnvironment()}/api/sso/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ user: username, password, verificationCode: otp }),
  })
  return {
    success: apiResponse.status === 200,
    text: apiResponse.statusText,
    csrftoken: (await apiResponse.json()).token,
  }
}
export function auth0Login(username?: string, connection?: string): void {
  const settings = getSSOConfig()
  new WebAuth({
    domain: settings.domain,
    clientID: settings.clientId,
  }).authorize({
    responseType: 'code',
    scope: settings.scope,
    redirectUri: settings.redirectUri,
    audience: settings.audience,
    login_hint: username,
    connection: connection ?? settings.defaultConnection,
  })
}
export function ssoLogin(connection: string | undefined): void {
  const settings = getSSOConfig()
  const client = new WebAuth({
    domain: settings.domain,
    clientID: settings.clientId,
  })
  if (connection != undefined) {
    client.authorize({
      connection,
      responseType: 'code',
      scope: settings.scope,
      redirectUri: settings.redirectUri,
      audience: settings.audience,
    })
  }
}

export async function logoutAuth0(returnTo: string | undefined): Promise<void> {
  const settings = getSSOConfig()
  new WebAuth({ domain: settings.domain, clientID: settings.clientId }).logout({
    returnTo,
  })
}

export async function logoutSession(): Promise<void> {
  const buildLogoutHeaders = (): Record<string, string> => {
    const token = getCSRFToken()
    return token ? { 'X-XSRF-TOKEN': token } : {}
  }
  const res = await fetch(`${getApiEnvironment()}/api/sso/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: buildLogoutHeaders(),
  })
  if (res.status == 418) {
    // pretend csrf retry
    let info
    try {
      info = await getUserInfo()
    } catch (error) {
      window.location.href = getLoginURLWithRedirectString()
      return
    }
    setCSRFToken(info?.csrfToken)

    await fetch(`${getApiEnvironment()}/api/sso/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: buildLogoutHeaders(),
    })
  }
}

export async function logout(redirectApp: string): Promise<void> {
  await logoutSession()
  await logoutAuth0(
    `${window.location.protocol}//${window.location.hostname}:${window.location.port}/login?redirect=${redirectApp}`
  )
}

export async function getOidcConfig() {
  const settings = getSSOConfig()
  // Auth0 does not publish an end session endpoint
  return await fetch(`${settings.domain}/.well-known/openid-configuration`)
}

export async function getSSOSettings(username: string): Promise<LoginSettingsResponse> {
  const response = await fetch(
    `${getApiEnvironment()}/api/sso/auth/login-settings/${encodeURIComponent(username)}` // /api/sso/profile/ssoconnectionname?email=${encodeURIComponent(username)}`
  )
  const { status } = response
  if (status === 200) {
    return await response.json()
  } else {
    if (status === 404) {
      throw 'Invalid credential'
    } else {
      throw 'Something went wrong. Please try again later.'
    }
  }
}

export async function sendOTP(username: string): Promise<void> {
  try {
    await sendMFACode(username)
  } catch (error) {
    throw 'Unable to send OTP to user'
  }
}

export async function getUserInfo(): Promise<UserInfoResponse> {
  const csrfToken = getCSRFToken()
  const response = await fetch(`${getApiEnvironment()}/api/sso/auth/userinfo`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}),
    },
  })
  if (response.status !== 200) {
    throw 'Unable to get userinfo'
  }
  return await response.json()
}

export async function forgotPassword(username: string, productName: string): Promise<LoginResponse> {
  try {
    const apiResponse = await fetch(`${getApiEnvironment()}/api/sso/auth/request-password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userName: username /* Capitalize */, productName }),
    })

    if (!apiResponse.ok) {
      const errorData: ErrorResponse = await apiResponse.json()
      return {
        success: false,
        text: errorData.message || apiResponse.statusText,
        errorCode: errorData.errorCode,
      }
    }

    return {
      success: true,
      text: 'Success',
    }
  } catch (error) {
    return {
      success: false,
      text: 'An unexpected error occurred. Please try again.',
    }
  }
}

export interface ResetPasswordResponse {
  userName: string
  password: string
}

export async function resetPassword(token: string): Promise<ResetPasswordResponse> {
  const apiResponse = await fetch(`${getApiEnvironment()}/api/sso/auth/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accountResetToken: token }),
  })

  if (apiResponse.status !== 200) {
    throw apiResponse.statusText
  }

  return await apiResponse.json()
}

export async function SAMLSessionLogin(authorizationCode: string, idToken: string): Promise<SAMLLoginResponse> {
  const res = await fetch(
    `${getApiEnvironment()}/api/sso/auth/auth0_login?` +
      new URLSearchParams({
        authorizationCode,
        idToken,
      }),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }
  )
  return res.json()
}

export async function OIDCSessionLogin(
  authorizationCode: string,
  state: string,
  redirectUrl: string
): Promise<OIDCAccessTokenResponse> {
  const res = await fetch(
    `${getApiEnvironment()}/api/sso/auth/token/oidctoken?` +
      new URLSearchParams({
        authorization_code: authorizationCode,
        state,
        redirectUrl,
      }),
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }
  )
  return res.json()
}

export async function refreshOIDCToken(refreshToken: string): Promise<OIDCAccessTokenResponse> {
  const res = await fetch(`${getApiEnvironment()}/api/sso/auth/token/refreshoidctoken`, {
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
  return res?.json()
}

export interface AuthTokens {
  csrfToken: string | null
}

export function getAuthTokensFromLocalStorage(): AuthTokens {
  return {
    csrfToken: getCSRFToken(),
  }
}

export async function refreshAuthTokens(): Promise<void> {
  try {
    const userInfo = await getUserInfo()
    const csrfToken = userInfo?.csrfToken
    if (csrfToken) {
      setCSRFToken(csrfToken)
    } else {
      removeCSRFToken()
    }
  } catch (error) {
    // they are unauthenticated
    window.location.href = getLoginURLWithRedirectString()
  }
}
